import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Button, CircularProgress, Alert, Grid, TextField, Paper
} from '@mui/material';
import { submitInquiry } from '../../api/inquiryService';

const initialFormData = {
  contactDetails: { name: '', email: '', phone: '' },
  tripDetails: {
    travelers: { adults: 1, children: 0 },
    dates: '',
    duration: '',
    budget: '',
  },
  expertConsultationNotes: '',
};

const ConsultationForm = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [section, field] = name.split('.');
      setFormData(p => ({
        ...p,
        [section]: { ...p[section], [field]: value },
      }));
    } else {
      setFormData(p => ({ ...p, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        inquiryType: 'Expert Consultation',
        ...formData,
      };
      await submitInquiry(payload);
      navigate('/plan-your-trip/thank-you');
    } catch (err) {
      setError('Submission failed. Please check your details and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ py: 8, backgroundColor: 'grey.50' }}>
      <Container maxWidth="md">
        <Typography variant="h3" component="h1" align="center" gutterBottom fontWeight="bold">
          Let's Plan Your Trip Together
        </Typography>
        <Typography variant="h6" color="text.secondary" align="center" sx={{ mb: 6 }}>
          Share your travel dreams with us, and our experts will craft a personalized proposal for you.
        </Typography>

        <Paper component="form" onSubmit={handleSubmit} sx={{ p: 4 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={3}>
            <Grid item size={{ xs: 12, sm: 6 }}>
              <TextField required fullWidth label="Full Name" name="contactDetails.name" value={formData.contactDetails.name} onChange={handleChange} />
            </Grid>
            <Grid item size={{ xs: 12, sm: 6 }}>
              <TextField required fullWidth label="Email Address" name="contactDetails.email" type="email" value={formData.contactDetails.email} onChange={handleChange} />
            </Grid>
            <Grid item xs={12}>
              <TextField required fullWidth label="Phone Number" name="contactDetails.phone" value={formData.contactDetails.phone} onChange={handleChange} />
            </Grid>
            <Grid item size={{ xs: 12, sm: 6 }}>
              <TextField required fullWidth label="Number of Travelers (Adults)" name="tripDetails.travelers.adults" type="number" InputProps={{ inputProps: { min: 1 } }} value={formData.tripDetails.travelers.adults} onChange={handleChange} />
            </Grid>
             <Grid item size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Approximate Budget" name="tripDetails.budget" value={formData.tripDetails.budget} onChange={handleChange} helperText="e.g., $5000 USD per person"/>
            </Grid>
            <Grid item size={{ xs: 12, sm: 6 }}>
              <TextField required fullWidth label="Preferred Travel Dates" name="tripDetails.dates" value={formData.tripDetails.dates} onChange={handleChange} helperText="e.g., Mid-August 2024"/>
            </Grid>
            <Grid item size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Desired trip duration" name="tripDetails.duration" value={formData.tripDetails.duration} onChange={handleChange} helperText="e.g., 10 days, 2 weeks"/>
            </Grid>
            <Grid item size={12}>
              <TextField required fullWidth multiline rows={6} label="Tell us about your ideal trip" name="expertConsultationNotes" value={formData.expertConsultationNotes} onChange={handleChange} placeholder="What are your must-see destinations? What kind of experiences are you looking for?"/>
            </Grid>
            <Grid item size={12} sx={{ textAlign: 'center' }}>
              <Button type="submit" variant="contained" size="large" disabled={submitting} sx={{ backgroundColor: '#D4A259', '&:hover': { backgroundColor: '#b88b4a' } }}>
                {submitting ? <CircularProgress size={24} color="inherit" /> : 'Request My Consultation'}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
};

export default ConsultationForm;